import React from 'react';
import PropTypes from 'prop-types';
import { forgeClassHelper } from '../utils/classes';

const classes = forgeClassHelper('checkbox');

const CheckboxInput = React.forwardRef((props, ref) => {
  const {
    error,
    hideRequiredStyles,
    id,
    indeterminate,
    required,
    defaultChecked,
    disabled,
    'aria-labelledby': ariaLabelledby,
    ...inputProps
  } = props;

  const useErrorStyles = error && !disabled;
  const useRequiredStyles = required && !hideRequiredStyles && !disabled;

  return (
    // Label is only being used here for form control
    <label
      {...classes({
        element: 'form-control-label',
      })}
    >
      <input
        type="checkbox"
        {...inputProps}
        {...classes({
          element: 'input',
          states: {
            disabled,
            error: useErrorStyles,
            indeterminate,
          },
        })}
        aria-labelledby={ariaLabelledby}
        aria-checked={indeterminate ? 'mixed' : props.checked}
        id={id}
        disabled={disabled}
        defaultChecked={defaultChecked}
        indeterminate={indeterminate ? indeterminate.toString() : undefined}
        ref={ref}
        required={required}
      />
      <div
        id={`checkbox-mark-${id}`}
        {...classes({
          element: 'input-marker',
          states: {
            disabled,
            error: useErrorStyles,
            required: useRequiredStyles,
            indeterminate,
          },
        })}
      />
    </label>
  );
});

CheckboxInput.displayName = 'CheckboxInput';

// This is only used by the form-redux-form package and can probably be removed in Forge 4
CheckboxInput.validateRequired = (value, options) => {
  if (value !== true) {
    return (options && options.message) || 'This field is required.';
  }
};

CheckboxInput.propsFromValue = (value) => ({ checked: !!value });

CheckboxInput.disableLabelFor = true;

CheckboxInput.propTypes = {
  /** Specify an additional element ID to label the input. (The input is automatically labeled by the input label, so specifying this additional label target merely provides an additional label.) */
  'aria-labelledby': PropTypes.string,
  /** Whether the Checkbox should be checked. Used for [controlled](https://reactjs.org/docs/forms.html#controlled-components) components. */
  checked: PropTypes.bool,
  /** Adds a class to the root element of the component */
  className: PropTypes.string,
  /** Whether the Checkbox should be checked. Used for [uncontrolled](https://reactjs.org/docs/uncontrolled-components.html) Checkboxes. */
  defaultChecked: PropTypes.bool,
  /** Adds the relevant Forge state class to the input and passes the prop down to the native react `<input />` */
  disabled: PropTypes.bool,
  /** Highlights the input when defined (specific string doesn't matter) */
  error: PropTypes.string,
  /** Hides any required styling. Can be set manually or by the required field variation set for `Form`. */
  hideRequiredStyles: PropTypes.bool,
  /** Id is sent to the `<input />`, and is used to associate the label with the checkbox. */
  id: PropTypes.string.isRequired,
  /** If the checkbox is neither true nor false */
  indeterminate: PropTypes.bool,
  /** Indicates if it is a required field. Passed as an attribute to the native react `<input />` */
  required: PropTypes.bool,
  /** Passthrough props to the `<input>` element */
  '...rest': PropTypes.any,
};

CheckboxInput.defaultProps = {
  disabled: false,
  required: false,
};

export default CheckboxInput;
